import { ErrorMessage, Field, Form, Formik } from "formik";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FUEL_TYPE,
  normaliseFuelType,
  normaliseMeterType,
  SAME_SUPPLIER,
  SAME_TARIFF,
  SUPPLIER_FUEL_TYPE,
} from "../../constants/tariffInfo";
import { useTariff } from "../../context/TariffContext";
import EN from "../../locales/en";
import createEditTariffFormYupSchema from "../../utils/createEditTariffFormYupSchema";
import Button from "../Button";
import ErrorIcon from "../icons/ErrorIcon";
import Select from "../Select";
import EditEnergyConsumption from "./EditEnergyConsumption";
import EditFuelTariffFields from "./EditFuelTariffFields";
import EditSharedSupplierTariffFields from "./EditSharedSupplierTariffFields";

const EditTariffForm = ({ setShowEditTariffForm, handleSubmit = null }) => {
  const {
    hasCompleteTariffInfo,
    combinedTariffInfo,
    setUserDefinedTariffInfo,
    tariffOptions,
    supplierOptions,
    updateSupplierOptions,
    selectedFuelType,
    selectedMeterTypes,
    selectedPaymentMethods,
    selectedSuppliers,
    setSelectedSuppliers,
    setSelectedFuelType,
    setSelectedMeterTypes,
    setSelectedPaymentMethods,
    currentTariff,
    updateTariffOptions,
  } = useTariff();

  const navigate = useNavigate();

  const [frequency, setFrequency] = useState("monthly");

  useEffect(() => {
    if (!currentTariff) return;
    setSelectedFuelType(normaliseFuelType(currentTariff?.fuelType));
    setSelectedMeterTypes({
      [SUPPLIER_FUEL_TYPE.ELECGAS]: currentTariff?.meterTypeElec || "",
      [SUPPLIER_FUEL_TYPE.ELEC]: currentTariff?.meterTypeElec || "",
      [SUPPLIER_FUEL_TYPE.GAS]: currentTariff?.meterTypeGas || "",
    });
    setSelectedPaymentMethods({
      [SUPPLIER_FUEL_TYPE.ELECGAS]:
        currentTariff?.existingElecPaymentMethod || "",
      [SUPPLIER_FUEL_TYPE.ELEC]: currentTariff?.existingElecPaymentMethod || "",
      [SUPPLIER_FUEL_TYPE.GAS]: currentTariff?.existingGasPaymentMethod || "",
    });
    setSelectedSuppliers({
      [SUPPLIER_FUEL_TYPE.ELECGAS]:
        currentTariff?.existingSupplierNameElec || "",
      [SUPPLIER_FUEL_TYPE.ELEC]: currentTariff?.existingSupplierNameElec || "",
      [SUPPLIER_FUEL_TYPE.GAS]: currentTariff?.existingSupplierNameGas || "",
    });
  }, [currentTariff]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const fetchTariffOptions = async () => {
      await updateTariffOptions();
    };

    fetchTariffOptions();
  }, [selectedSuppliers, supplierOptions]); // eslint-disable-line react-hooks/exhaustive-deps

  // when user selects a new fuel / meter type / payment method, fetch the relevant supplier options
  useEffect(() => {
    if (!currentTariff?.fuelType) return;

    const fetchSupplierOptions = async () => {
      await updateSupplierOptions(
        selectedFuelType || currentTariff?.fuelType || null,
        selectedMeterTypes[SUPPLIER_FUEL_TYPE.ELEC] ||
          currentTariff?.meterTypeElec ||
          null,
        selectedMeterTypes[SUPPLIER_FUEL_TYPE.GAS] ||
          currentTariff?.meterTypeGas ||
          null,
        selectedPaymentMethods[SUPPLIER_FUEL_TYPE.ELEC] ||
          currentTariff?.existingElecPaymentMethod ||
          null,
        selectedPaymentMethods[SUPPLIER_FUEL_TYPE.GAS] ||
          currentTariff?.existingGasPaymentMethod ||
          null
      );
      await updateTariffOptions();
    };

    fetchSupplierOptions();
  }, [selectedFuelType, selectedMeterTypes, selectedPaymentMethods]); // eslint-disable-line react-hooks/exhaustive-deps

  const filterSuppliers = (fuelType) => {
    const supplierOptionsByFuelType =
      supplierOptions[normaliseFuelType(fuelType)] || [];
    return supplierOptionsByFuelType.sort((a, b) =>
      a.label.localeCompare(b.label)
    );
  };

  const filterTariffsByPaymentType = (tariffs, paymentMethod) => {
    if (!tariffs?.length) return [];
    return tariffs.filter((tariff) =>
      tariff.paymentMethods?.includes(paymentMethod)
    );
  };

  const filterTariffsByMeterType = (tariffs, meterType) => {
    if (!tariffs?.length) return [];
    return tariffs.filter((tariff) =>
      tariff.meterTypes?.includes(normaliseMeterType(meterType))
    );
  };

  const filterTariffs = (fuelType, meterType, paymentMethod, supplierName) => {
    if (
      !tariffOptions ||
      !fuelType ||
      !meterType ||
      !paymentMethod ||
      !supplierName ||
      !supplierOptions
    ) {
      return [];
    }

    if (!supplierOptions[normaliseFuelType(fuelType)]) {
      return [];
    }

    const supplierId = supplierOptions[normaliseFuelType(fuelType)].find(
      (supplier) => supplier.label === supplierName
    )?.id;
    const tariffs =
      tariffOptions?.[normaliseFuelType(fuelType)]?.[supplierId] || [];
    const newTariffOptions = tariffs.map((tariff) => ({
      ...tariff,
      id: tariff.id,
      value: tariff.name,
      label: tariff.name,
    }));

    return filterTariffsByPaymentType(
      filterTariffsByMeterType(newTariffOptions, meterType),
      paymentMethod
    );
  };

  const closeEditTariffForm = () => {
    // if the user does not have all the required field, send them back to the postcode page
    if (!hasCompleteTariffInfo) {
      navigate("/address");
    }
    setShowEditTariffForm(false);
  };

  const handleFrequencyChange = (newFrequency) => {
    setFrequency(newFrequency);
  };

  const FuelTypeMapping = {
    [FUEL_TYPE.ELECGAS]: ["electricity", "gas"],
    [FUEL_TYPE.ELEC]: ["electricity"],
    [FUEL_TYPE.GAS]: ["gas"],
    [SUPPLIER_FUEL_TYPE.ELECGAS]: ["electricity", "gas"],
    [SUPPLIER_FUEL_TYPE.ELEC]: ["electricity"],
    [SUPPLIER_FUEL_TYPE.GAS]: ["gas"],
  };

  const fuelTypesArray = FuelTypeMapping[combinedTariffInfo?.fuelType] || [];

  const sameSupplier =
    fuelTypesArray.length > 1 &&
    combinedTariffInfo?.existingSupplierNameElec &&
    combinedTariffInfo?.existingSupplierNameGas &&
    combinedTariffInfo?.existingSupplieIdElec ===
      combinedTariffInfo?.existingSupplieIdGas
      ? SAME_SUPPLIER.SINGLE
      : SAME_SUPPLIER.SEPARATE;

  const sameTariff =
    fuelTypesArray.length > 1 &&
    combinedTariffInfo?.existingTariffIdElec &&
    combinedTariffInfo?.existingTariffIdGas &&
    combinedTariffInfo?.existingTariffIdElec ===
      combinedTariffInfo?.existingTariffIdGas
      ? SAME_TARIFF.YES
      : SAME_TARIFF.NO;

  const tariffs = [
    normaliseFuelType(combinedTariffInfo?.fuelType) !== SUPPLIER_FUEL_TYPE.GAS
      ? {
          fuel: "electricity",
          meterType: combinedTariffInfo?.meterTypeElec || "",
          supplierName: combinedTariffInfo?.existingSupplierNameElec || "",
          paymentMethod: combinedTariffInfo?.existingElecPaymentMethod || "",
          tariffName: combinedTariffInfo?.existingTariffNameElec || "",
          estimatedUsage: {
            value:
              frequency === "annually"
                ? parseFloat(combinedTariffInfo?.existingkWhsElec).toFixed(2)
                : (
                    parseFloat(combinedTariffInfo?.existingkWhsElec) / 12
                  ).toFixed(2) || 0,
            unit: frequency,
          },
        }
      : {},
    normaliseFuelType(combinedTariffInfo?.fuelType) !== SUPPLIER_FUEL_TYPE.ELEC
      ? {
          fuel: "gas",
          meterType: combinedTariffInfo?.meterTypeGas || "",
          supplierName: combinedTariffInfo?.existingSupplierNameGas || "",
          paymentMethod: combinedTariffInfo?.existingGasPaymentMethod || "",
          tariffName: combinedTariffInfo?.existingTariffNameGas || "",
          estimatedUsage: {
            value:
              frequency === "annually"
                ? parseFloat(combinedTariffInfo?.existingkWhsGas).toFixed(2)
                : (
                    parseFloat(combinedTariffInfo?.existingkWhsGas) / 12
                  ).toFixed(2) || 0,
            unit: frequency,
          },
        }
      : {},
  ];

  const initialValues = {
    fuelTypes: combinedTariffInfo?.fuelType,
    sameSupplier: sameSupplier,
    sameTariff: sameTariff,
    tariffs: tariffs,
  };

  const getSupplierId = (supplierName, fuelType) => {
    const suppliers = filterSuppliers(fuelType);

    if (!suppliers) {
      return null;
    }
    return (
      suppliers.find((supplier) => supplier.label === supplierName)?.id || ""
    );
  };

  const getTariffId = (
    meterType,
    paymentMethod,
    supplierName,
    tariffName,
    fuelType
  ) => {
    const tariffs = filterTariffs(
      normaliseFuelType(fuelType),
      meterType,
      paymentMethod,
      supplierName
    );

    return tariffs.find((t) => t.name === tariffName)?.id || "";
  };

  const submitWrapper = async (values) => {
    try {
      const newUserDefinedTariffInfo = {
        fuelType: values.fuelTypes,
      };

      if (handleSubmit) {
        await handleSubmit(values);
      }

      const updateTariffInfo = (tariff, fuelType, targetInfo) => {
        targetInfo[`meterType${fuelType}`] = tariff?.meterType || "";
        targetInfo[`existingSupplierName${fuelType}`] =
          tariff?.supplierName || "";
        targetInfo[`existingSupplieId${fuelType}`] =
          getSupplierId(
            tariff?.supplierName,
            normaliseFuelType(values.fuelTypes) ===
              SUPPLIER_FUEL_TYPE.ELECGAS &&
              values.sameSupplier === SAME_SUPPLIER.SINGLE
              ? SUPPLIER_FUEL_TYPE.ELECGAS
              : fuelType
          ).toString() || null;
        targetInfo[`existing${fuelType}PaymentMethod`] =
          tariff?.paymentMethod || "";
        targetInfo[`existingTariffName${fuelType}`] = tariff?.tariffName || "";
        targetInfo[`existingTariffId${fuelType}`] =
          getTariffId(
            tariff?.meterType,
            tariff?.paymentMethod,
            tariff?.supplierName,
            tariff?.tariffName,
            normaliseFuelType(values.fuelTypes) ===
              SUPPLIER_FUEL_TYPE.ELECGAS &&
              values.sameTariff === SAME_TARIFF.YES
              ? SUPPLIER_FUEL_TYPE.ELECGAS
              : fuelType
          ).toString() || null;
        targetInfo[`existingkWhs${fuelType}`] =
          tariff?.estimatedUsage?.unit === "annually"
            ? parseFloat(tariff?.estimatedUsage?.value)
            : parseFloat(tariff?.estimatedUsage?.value * 12) || 0;
      };

      if (
        values.fuelTypes === FUEL_TYPE.ELECGAS ||
        values.fuelTypes === FUEL_TYPE.ELEC
      ) {
        const elecTariff = values.tariffs[0] || {};
        updateTariffInfo(elecTariff, "Elec", newUserDefinedTariffInfo);
      }

      if (
        values.fuelTypes === FUEL_TYPE.ELECGAS ||
        values.fuelTypes === FUEL_TYPE.GAS
      ) {
        const gasTariff = values.tariffs[1] || {};
        updateTariffInfo(gasTariff, "Gas", newUserDefinedTariffInfo);
      }

      if (values.fuelTypes === FUEL_TYPE.ELECGAS) {
        if (values.sameSupplier) {
          newUserDefinedTariffInfo["singleOrSeparate"] = values.sameSupplier;
          newUserDefinedTariffInfo["sameTariff"] = values.sameTariff;
        }
        if (values.sameTariff === SAME_TARIFF.YES) {
          // if the user selects the same tariff for both fuels, set the gas tariff id to the same as the electricity tariff id. Avoids issues when user has E7 meter.
          newUserDefinedTariffInfo["existingTariffIdGas"] =
            newUserDefinedTariffInfo["existingTariffIdElec"];
        }
      }

      setUserDefinedTariffInfo(newUserDefinedTariffInfo);

      setShowEditTariffForm(false);
    } catch (errors) {
      console.error(errors);
    }
  };

  const validationSchema = createEditTariffFormYupSchema();

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={submitWrapper}
    >
      {(formik) => (
        <Form className="space-y-8">
          <div className="mb-6">
            <h2 className="text-[30px] font-bold text-primary">
              {EN.edit_tariff_form.title}
            </h2>
            <div className="mt-3 border-2 border-primary rounded-[8px] p-4">
              <p>{EN.edit_tariff_form.subtitle}</p>
            </div>
          </div>
          <h3 className="text-[22px] font-bold">
            {EN.edit_tariff_form.fuel_type.title}
          </h3>
          <div className="mt-3 space-y-6">
            <Field
              as={Select}
              id="fuel-type-select"
              options={[
                {
                  value: FUEL_TYPE.ELECGAS,
                  label: EN.edit_tariff_form.fuel_type.options.both,
                },
                {
                  value: FUEL_TYPE.ELEC,
                  label: EN.edit_tariff_form.fuel_type.options.electricity,
                },
                {
                  value: FUEL_TYPE.GAS,
                  label: EN.edit_tariff_form.fuel_type.options.gas,
                },
              ]}
              value={formik.values.fuelTypes}
              onChange={(e) => {
                setSelectedFuelType(normaliseFuelType(e.target.value));
                formik.setFieldValue("fuelTypes", e.target.value);
                if (e.target.value === FUEL_TYPE.ELEC) {
                  formik.setFieldValue("tariffs.1", {});
                } else if (e.target.value === FUEL_TYPE.GAS) {
                  formik.setFieldValue("tariffs.0", {});
                } else {
                  formik.setFieldValue("tariffs.1.fuel", "gas");
                  formik.setFieldValue("tariffs.0.fuel", "electricity");
                  if (formik.values.sameSupplier === SAME_SUPPLIER.SINGLE) {
                    formik.setFieldValue(
                      "tariffs.1.paymentMethod",
                      formik.values.tariffs[0].paymentMethod
                    );
                  }
                }
              }}
              hasErrors={formik.errors.fuelTypes}
              disabled={formik.isSubmitting}
            />
          </div>
          {formik.values.fuelTypes === FUEL_TYPE.ELECGAS && (
            <div className="border-t border-[#afb6b9] pt-6">
              <h3
                className={`text-[22px] font-bold ${formik.errors.sameSupplier ? "text-red-500" : ""}`}
              >
                {EN.edit_tariff_form.same_supplier.title}
              </h3>
              <div className="mt-4 space-y-4">
                <label className="flex items-start gap-2">
                  <Field
                    type="radio"
                    className={`mt-1 h-4 w-4 rounded-full text-primary focus:ring-primary ${formik.errors.sameSupplier ? "border-red-500" : "border-gray-300"}`}
                    name="sameSupplier"
                    value={SAME_SUPPLIER.SINGLE}
                    checked={
                      formik.values.sameSupplier === SAME_SUPPLIER.SINGLE
                    }
                    onChange={() => {
                      formik.setFieldValue(
                        "sameSupplier",
                        SAME_SUPPLIER.SINGLE
                      );
                      formik.setFieldValue(
                        "tariffs.1.supplierName",
                        formik.values.tariffs[0].supplierName
                      );
                      formik.setFieldValue(
                        "tariffs.1.paymentMethod",
                        formik.values.tariffs[0].paymentMethod
                      );
                      setSelectedSuppliers({
                        [SUPPLIER_FUEL_TYPE.ELECGAS]:
                          formik.values.tariffs[0].supplierName,
                        [SUPPLIER_FUEL_TYPE.ELEC]:
                          formik.values.tariffs[0].supplierName,
                        [SUPPLIER_FUEL_TYPE.GAS]:
                          formik.values.tariffs[0].supplierName,
                      });
                      setSelectedMeterTypes({
                        [SUPPLIER_FUEL_TYPE.ELECGAS]:
                          formik.values.tariffs[0].meterType,
                        [SUPPLIER_FUEL_TYPE.ELEC]:
                          formik.values.tariffs[0].meterType,
                        [SUPPLIER_FUEL_TYPE.GAS]:
                          formik.values.tariffs[0].meterType,
                      });
                      setSelectedPaymentMethods({
                        [SUPPLIER_FUEL_TYPE.ELECGAS]:
                          formik.values.tariffs[0].paymentMethod,
                        [SUPPLIER_FUEL_TYPE.ELEC]:
                          formik.values.tariffs[0].paymentMethod,
                        [SUPPLIER_FUEL_TYPE.GAS]:
                          formik.values.tariffs[0].paymentMethod,
                      });
                    }}
                    disabled={formik.isSubmitting}
                  />
                  <span className="flex-1">
                    {EN.edit_tariff_form.same_supplier.options.single}
                  </span>
                </label>

                <label className="flex items-start gap-2">
                  <Field
                    type="radio"
                    name="sameSupplier"
                    className={`mt-1 h-4 w-4 rounded-full text-primary focus:ring-primary ${formik.errors.sameSupplier ? "border-red-500" : "border-gray-300"}`}
                    value={SAME_SUPPLIER.SEPARATE}
                    checked={
                      formik.values.sameSupplier === SAME_SUPPLIER.SEPARATE
                    }
                    onChange={() => {
                      formik.setFieldValue(
                        "sameSupplier",
                        SAME_SUPPLIER.SEPARATE
                      );
                      formik.setFieldValue("sameTariff", SAME_TARIFF.NO);
                    }}
                    disabled={formik.isSubmitting}
                  />
                  <span className="flex-1">
                    {EN.edit_tariff_form.same_supplier.options.separate}
                  </span>
                </label>
              </div>

              {formik.errors.sameSupplier && (
                <ErrorMessage
                  name="sameSupplier"
                  component={(props) => (
                    <div className="mt-3 flex justify-start items-start">
                      <ErrorIcon />
                      <div className="text-red-500 text-sm">
                        {props.children}
                      </div>
                    </div>
                  )}
                />
              )}
            </div>
          )}
          {formik.values.fuelTypes &&
            (formik.values.fuelTypes !== FUEL_TYPE.ELECGAS ||
              (formik.values.fuelTypes === FUEL_TYPE.ELECGAS &&
                formik.values.sameSupplier === SAME_SUPPLIER.SEPARATE)) &&
            FuelTypeMapping[formik.values.fuelTypes].map((fuelType) => (
              <div key={`${fuelType}`}>
                <EditFuelTariffFields
                  formik={formik}
                  fuelType={fuelType}
                  filterTariffs={filterTariffs}
                  filterSuppliers={filterSuppliers}
                />
              </div>
            ))}
          {formik.values.fuelTypes === FUEL_TYPE.ELECGAS &&
            formik.values.sameSupplier === SAME_SUPPLIER.SINGLE && (
              <EditSharedSupplierTariffFields
                formik={formik}
                filterTariffs={filterTariffs}
                filterSuppliers={filterSuppliers}
              />
            )}
          <EditEnergyConsumption
            formik={formik}
            handleFrequencyChange={handleFrequencyChange}
            FuelTypeMapping={FuelTypeMapping}
            frequency={frequency}
          />
          <div className="mt-6 border-t border-[#afb6b9] pt-8">
            <div className="flex justify-between">
              <Button
                type="button"
                variant="secondary"
                onClick={() => closeEditTariffForm()}
              >
                {EN.edit_tariff_form.buttons.cancel}
              </Button>
              <Button
                type="submit"
                disabled={
                  formik.errors.electricityUsage ||
                  formik.errors.gasUsage ||
                  formik.isSubmitting
                }
              >
                {EN.edit_tariff_form.buttons.update}
              </Button>
            </div>
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default EditTariffForm;
